import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import {
  MdOutlineLocationCity,
  MdOutlineMap,
  MdOutlineMarkunreadMailbox,
  MdOutlinePlace,
  MdSearch,
} from "react-icons/md";

import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import TextInput from "../../../../../../../Components/UI/Form/TextInput";
import SegmentedControl from "../../../../../../../Components/UI/SegmentedControl/SegmentedControl";
import { areasFor, codesWithin, searchPlaces } from "./postalAreas";

const PLACE_LIMIT = 5;
const ADDRESS_LIMIT = 3;
const ADDRESS_DELAY_MS = 250;
const CODE_TOKEN = /^[A-Z]\d[A-Z]?$/;
const FULL_POSTAL_CODE = /\b([A-Z]\d[A-Z])\s?\d[A-Z]\d\b/g;
const DISTANCES = ["5", "10", "25", "50", "100"].map((value) => ({
  value,
  label: `${value} km`,
}));
const DEFAULT_DISTANCE = "10";

const KIND_LABELS = { metro: "Metro area", province: "Province" };
const ICONS = {
  codes: MdOutlineMarkunreadMailbox,
  city: MdOutlineLocationCity,
  metro: MdOutlineLocationCity,
  province: MdOutlineMap,
  address: MdOutlinePlace,
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const parseCodes = (text) =>
  text
    .toUpperCase()
    .replace(FULL_POSTAL_CODE, "$1")
    .split(/[\s,;]+/)
    .filter(Boolean);

const placesApi = () => window.google?.maps?.places;

function DistancePicker({ areas, pick, onAdd, onCancel, onPreview }) {
  const [distance, setDistance] = useState(DEFAULT_DISTANCE);
  const km = Number(distance);
  const codes = useMemo(
    () => codesWithin(areas, pick.center, km),
    [areas, pick, km],
  );

  useEffect(() => {
    onPreview({ center: pick.center, km, codes });
  }, [pick, km, codes, onPreview]);
  useEffect(() => () => onPreview(null), [onPreview]);

  return (
    <div className="flex flex-col gap-[8px] rounded-[7px] border-[1px] border-brand bg-ui-surface p-[10px]">
      <div className="flex items-start gap-[6px]">
        <MdOutlinePlace className="mt-[1px] h-[16px] w-[16px] shrink-0 text-brand" />
        <span className="min-w-[0px] flex-1 font-manrope text-[12px] font-semibold leading-[1.35] text-ui-text">
          {pick.label}
        </span>
        <button
          type="button"
          aria-label="Cancel"
          onClick={onCancel}
          className="grid h-[20px] w-[20px] shrink-0 place-items-center rounded-[4px] text-ui-text-muted hover:bg-ui-surface-sunken hover:text-ui-text"
        >
          <IoClose className="h-[14px] w-[14px]" />
        </button>
      </div>
      <SegmentedControl
        options={DISTANCES}
        value={distance}
        onChange={setDistance}
        ariaLabel="Distance"
        className="self-start"
      />
      <BoltButton
        type="button"
        variant="primary"
        size="sm"
        label={`Add ${plural(codes.length, "area")} within ${km} km`}
        onClick={() => onAdd(codes)}
        disabled={!codes.length}
        className="self-start"
      />
    </div>
  );
}

export default function AreaSearch({ areas, places, onAdd, onPreview }) {
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const [addressResult, setAddressResult] = useState({ query: "", list: [] });
  const [pick, setPick] = useState(null);
  const [error, setError] = useState(null);
  const serviceRef = useRef(null);
  const sessionRef = useRef(null);
  const requestRef = useRef(0);
  const listRef = useRef(null);

  const codes = useMemo(() => parseCodes(text), [text]);
  const looksLikeCodes =
    codes.length > 0 && codes.every((code) => CODE_TOKEN.test(code));

  useEffect(() => {
    const request = ++requestRef.current;
    const api = placesApi();
    if (looksLikeCodes || text.trim().length < 3 || !api) {
      setAddressResult({ query: text, list: [] });
      return undefined;
    }
    const timer = setTimeout(() => {
      if (!serviceRef.current) serviceRef.current = new api.AutocompleteService();
      if (!sessionRef.current) sessionRef.current = new api.AutocompleteSessionToken();
      serviceRef.current.getPlacePredictions(
        {
          input: text,
          componentRestrictions: { country: "ca" },
          sessionToken: sessionRef.current,
        },
        (predictions) => {
          if (request === requestRef.current) {
            setAddressResult({
              query: text,
              list: (predictions || []).slice(0, ADDRESS_LIMIT),
            });
          }
        },
      );
    }, ADDRESS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text, looksLikeCodes]);

  const items = useMemo(() => {
    if (!text.trim()) return [];
    if (looksLikeCodes) {
      const unknown = codes.filter((code) => !areasFor(areas, [code]).length);
      if (unknown.length) {
        return [
          {
            type: "message",
            label: `No postal area starts with ${unknown.join(", ")}`,
          },
        ];
      }
      return [
        {
          type: "codes",
          label: codes.join(", "),
          detail: `Postal code · ${plural(areasFor(areas, codes).length, "area")}`,
          prefixes: codes,
        },
      ];
    }
    const found = searchPlaces(places, text, PLACE_LIMIT).map((place) => ({
      type: place.kind,
      label: place.kind === "metro" ? `${place.name} metro area` : place.name,
      detail: [
        KIND_LABELS[place.kind],
        place.kind === "province" ? null : place.province,
        plural(place.count, "area"),
      ]
        .filter(Boolean)
        .join(" · "),
      prefixes: place.prefixes,
    }));
    const addresses =
      addressResult.query === text ? addressResult.list : [];
    const addressItems = addresses.map((prediction) => ({
      type: "address",
      label: prediction.structured_formatting?.main_text ?? prediction.description,
      detail: prediction.structured_formatting?.secondary_text ?? "Address",
      prediction,
    }));
    if (!found.length && !addressItems.length && addressResult.query === text) {
      return [{ type: "message", label: "No matching place or address" }];
    }
    return [...found, ...addressItems];
  }, [text, codes, looksLikeCodes, areas, places, addressResult]);

  const choosable = items.filter((item) => item.type !== "message");
  const hasItems = items.length > 0;

  useEffect(() => {
    if (hasItems) {
      listRef.current?.firstElementChild?.scrollIntoView?.({ block: "nearest" });
    }
  }, [hasItems]);
  const activeIndex = Math.min(active, Math.max(choosable.length - 1, 0));

  const reset = () => {
    setText("");
    setActive(0);
    setAddressResult({ query: "", list: [] });
    setError(null);
  };

  const pickAddress = ({ prediction }) => {
    const api = placesApi();
    if (!api) return;
    new api.PlacesService(document.createElement("div")).getDetails(
      {
        placeId: prediction.place_id,
        fields: ["geometry"],
        sessionToken: sessionRef.current,
      },
      (place) => {
        sessionRef.current = null;
        const location = place?.geometry?.location;
        if (!location) {
          setError("Couldn't find that address. Try another one.");
          return;
        }
        setPick({
          label: prediction.description,
          center: { lat: location.lat(), lng: location.lng() },
        });
        reset();
      },
    );
  };

  const choose = (item) => {
    if (!item || item.type === "message") return;
    if (item.type === "address") {
      pickAddress(item);
      return;
    }
    onAdd(item.prefixes);
    reset();
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!choosable.length) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((activeIndex + step + choosable.length) % choosable.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(choosable[activeIndex]);
    } else if (event.key === "Escape" && text) {
      event.stopPropagation();
      reset();
    }
  };

  return (
    <div className="flex flex-col gap-[6px]">
      <TextInput
        label="Add areas"
        name="routeAreaSearch"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setActive(0);
          setError(null);
        }}
        placeholder="City, address or postal code"
        prefix={<MdSearch className="h-[14px] w-[14px]" />}
        inputProps={{
          id: "routeAreaSearch",
          autoComplete: "off",
          role: "combobox",
          "aria-expanded": items.length > 0,
          "aria-controls": "routeAreaOptions",
          "aria-activedescendant": choosable.length
            ? `routeAreaOption-${activeIndex}`
            : undefined,
          onKeyDown,
        }}
      />
      {error && (
        <p
          role="alert"
          className="m-[0px] font-manrope text-[11px] font-semibold text-state-danger-text"
        >
          {error}
        </p>
      )}
      {items.length > 0 && (
        <ul
          ref={listRef}
          id="routeAreaOptions"
          role="listbox"
          aria-label="Suggestions"
          className="m-[0px] list-none overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface p-[0px] shadow-ui-md"
        >
          {items.map((item, index) => {
            if (item.type === "message") {
              return (
                <li
                  key="message"
                  className="px-[10px] py-[8px] font-manrope text-[11.5px] font-medium text-ui-text-muted"
                >
                  {item.label}
                </li>
              );
            }
            const position = choosable.indexOf(item);
            const Icon = ICONS[item.type];
            return (
              <li
                key={`${item.type}-${index}`}
                id={`routeAreaOption-${position}`}
                role="option"
                aria-selected={position === activeIndex}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(item);
                }}
                onMouseEnter={() => setActive(position)}
                className={`flex cursor-pointer items-center gap-[8px] px-[10px] py-[7px] font-manrope ${
                  position === activeIndex ? "bg-brand-light" : ""
                }`}
              >
                <Icon className="h-[16px] w-[16px] shrink-0 text-ui-text-muted" />
                <span className="min-w-[0px] flex-1">
                  <span className="block truncate text-[12px] font-semibold text-ui-text">
                    {item.label}
                  </span>
                  <span className="block truncate text-[10.5px] font-medium text-ui-text-muted">
                    {item.detail}
                  </span>
                </span>
                <span className="shrink-0 text-[11px] font-bold text-brand">
                  {item.type === "address" ? "Areas nearby" : "Add"}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {pick && (
        <DistancePicker
          areas={areas}
          pick={pick}
          onPreview={onPreview}
          onCancel={() => setPick(null)}
          onAdd={(found) => {
            onAdd(found);
            setPick(null);
          }}
        />
      )}
    </div>
  );
}
